import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  Box,
  CustomAutocomplete,
  TextFieldForm
} from 'web-library';
import { UseFormReturn } from 'react-hook-form';
import { ExamCandidateDetails } from '../../../../api/candidate/exam/model';
import { useStyles } from './style';
import ToggleDisplayEditFormItem from '../../../../components/ToggleDisplayEditFormItem';
import NationalityDetails from './NationalityDetails';
import AEISDetails from './AEISDetails';
import NameAndIdTypeDetails from './NameAndIdTypeDetails';
import { useGetIExamMasterCode } from '../../../../api-refactored/user/useGetIExamMasterCode';
import {
  getIExamDropdownItemLabel,
  getIExamMasterCodeSelected,
} from '../../../accountManagement/logic/useCreateCandidateForm';
import ControlledAutocomplete from '../../../../components/form-items/ControlledAutocomplete';
import {IExamMasterCodeItem} from "../../../../api/user/model";
import ControlledDatePicker from 'web-library/stories/atom/DatePicker/ControlledDatePicker';

/**
 * All the field in this component is disabled by default in edit state.
 * No requirement to allow the editing at the moment.
 *
 * @param examCandidateDetail candidate particular info
 * @param isEditState is editing state
 */

interface PersonlDetailsProps {
  examCandidateDetail: ExamCandidateDetails;
  isEditState?: boolean;
  sameRowName?: boolean;
  isAEIS?: boolean;
  isSPERS?: boolean;
  isOpsAdmin?: boolean;
  form?: UseFormReturn<Record<string, any>>;
  defaultExpanded?: boolean;
}

const PersonalDetails = ({
  examCandidateDetail,
  isEditState = false,
  sameRowName = false,
  isAEIS = false,
  isSPERS = false,
  isOpsAdmin = false,
  form,
  defaultExpanded = true,
}: PersonlDetailsProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const {
    dob,
    gender,
    race,
    occupation,
    schoolName,
    finPassport,
    passportNo,
    userIdType,
  } = examCandidateDetail;
  const { data: iExamsData } = useGetIExamMasterCode();

  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      header={t('exam->>ReviewExam.PersonalParticulars')}
    >
      <Box className={classes.container}>
        <NameAndIdTypeDetails
          examCandidateDetail={examCandidateDetail}
          isEditState={isEditState}
          sameRowName={sameRowName}
          isNonNationalExam={isAEIS || isSPERS}
          isOpsAdmin={isOpsAdmin}
          form={form}
        />
        {isAEIS && (
          <AEISDetails
            isEditState={isEditState}
            finPassport={finPassport}
            form={form}
            workPassType={examCandidateDetail.workPassType}
            userIdType={userIdType}
          />
        )}
        {isSPERS && (
          <ToggleDisplayEditFormItem
            isEditState={isEditState}
            labelText={t('exam->>ReviewExam.PassportNo')}
            valueText={passportNo}
            containerClassName={classes.formElementContainer}
            EditComponent={
              <TextFieldForm
                form={form}
                label={t('exam->>ReviewExam.PassportNo')}
                onChange={(e) => form?.setValue('passportNo', e.target.value.toUpperCase())}
                isRequired
                error={!!form?.formState.errors.passportNo}
                helperText={form?.formState.errors?.passportNo?.message?.toString()}
                name="passportNo"
                type="text"
                fullWidth
              />
            }
          />
        )}
        <ToggleDisplayEditFormItem
          isEditState={isEditState}
          labelText={t('exam->>ReviewExam.DateOfBirth')}
          valueText={dob}
          containerClassName={classes.formElementContainer}
          EditComponent={
            form && (
                <ControlledDatePicker
                    name="dateOfBirth"
                    control={form?.control}
                    defaultValue={dob}
                    label={t('exam->>ReviewExam.DateOfBirth')}
                    // onChange={(date) => onChange(date)}
                    inputVariant="filled"
                    disabled={!isOpsAdmin}
                    isRequired
                    disableFuture
                />
            )
          }
        />
        <ToggleDisplayEditFormItem
          isEditState={isEditState}
          labelText={t('exam->>ReviewExam.Gender')}
          valueText={gender}
          containerClassName={classes.formElementContainer}
          EditComponent={
            form && (
              <ControlledAutocomplete<IExamMasterCodeItem, false, false, undefined>
                label={t('exam->>ReviewExam.Gender')}
                id="input-select-gender"
                control={form.control}
                name="gender"
                disabled={!isOpsAdmin}
                error={!!form.formState.errors?.gender}
                helperText={form.formState.errors?.gender?.message?.toString()}
                options={iExamsData ? iExamsData.gender : []}
                getOptionLabel={getIExamDropdownItemLabel}
                isOptionEqualToValue={getIExamMasterCodeSelected}
                isRequired
              />
            )
          }
        />
        <ToggleDisplayEditFormItem
          isEditState={isEditState}
          labelText={t('exam->>ReviewExam.Race')}
          valueText={race}
          containerClassName={classes.formElementContainer}
          EditComponent={
            form && (
              <ControlledAutocomplete<IExamMasterCodeItem, false, false, undefined>
                label={t('exam->>ReviewExam.Race')}
                id="input-select-race"
                name="race"
                control={form.control}
                disabled={!isOpsAdmin}
                error={!!form.formState.errors?.race}
                helperText={form.formState.errors?.race?.message?.toString()}
                options={iExamsData ? iExamsData.race : []}
                getOptionLabel={getIExamDropdownItemLabel}
                isOptionEqualToValue={getIExamMasterCodeSelected}
                isRequired
              />
            )
          }
        />
        <NationalityDetails
          isNonNational={isAEIS || isSPERS}
          examCandidateDetail={examCandidateDetail}
          isEditState={isEditState}
          isOpsAdmin={isOpsAdmin}
          form={form}
        />
        {!isAEIS && !isSPERS && (
          <ToggleDisplayEditFormItem
            allHide
            isEditState={isEditState}
            labelText={t('exam->>ReviewExam.Occupation')}
            valueText={occupation}
            containerClassName={classes.formElementContainer}
            EditComponent={
              <CustomAutocomplete
                label={t('exam->>ReviewExam.Occupation')}
                id="input-select-occupation"
                value={occupation}
                fullWidth
                disabled
                options={[]}
              />
            }
          />
        )}
        <ToggleDisplayEditFormItem
          isEditState={isEditState}
          labelText={t('exam->>ReviewExam.School')}
          valueText={schoolName}
          containerClassName={`${classes.formElementContainer}`}
          EditComponent={
            form && (
              <CustomAutocomplete
                label={t('exam->>ReviewExam.School')}
                id="input-select-school"
                defaultValue={schoolName}
                fullWidth
                disabled
                options={[]}
              />
            )
          }
        />
      </Box>
    </Accordion>
  );
};

export default PersonalDetails;
